"use client";

import { LogOut, Settings, ArrowLeftRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useMutation } from "@tanstack/react-query";
import Link from "next/link";
import { hardNavigateIfAllowed } from "@/utils/editor-unsaved-guard";
import { logout } from "./logout-button";

export const NavUser = ({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar?: string;
    isAdmin?: boolean;
    canSwitchShop?: boolean;
    shop?: string;
    shopAvatar?: string;
  };
}) => {
  const { isMobile } = useSidebar();

  const { mutate: logoutMutate, isPending: isLogoutPending } = useMutation({
    mutationKey: ["logout"],
    mutationFn: () => logout(),
  });

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="default"
              className="h-10 gap-2 pl-2 data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-7 shrink-0 rounded-full">
                <AvatarImage
                  src={user.avatar}
                  alt={user.name}
                  className="object-contain"
                />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {user.name.slice(0, 1)}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 truncate text-left text-sm font-medium">
                {user.name}
              </span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width]"
            side={isMobile ? "bottom" : "top"}
            align="start"
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 bg-sidebar-accent">
                  <AvatarImage
                    src={user.avatar}
                    alt={user.name}
                    className="object-contain"
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user.name.slice(0, 1)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 gap-0.5 text-left leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            {user.canSwitchShop && (
              <DropdownMenuItem
                className="gap-2"
                onClick={() => void hardNavigateIfAllowed("/admin")}
              >
                <ArrowLeftRight className="size-3.5 text-muted-foreground" />
                Change Shop
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link
                href="/settings"
                className="flex w-full cursor-pointer items-center gap-2"
              >
                <Settings className="size-3.5 text-muted-foreground" />
                Settings
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="gap-2"
              onClick={() => logoutMutate()}
              disabled={isLogoutPending}
            >
              <LogOut className="size-3.5 text-muted-foreground" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
